import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import id from './locales/id';
import en from './locales/en';
import ms from './locales/ms';

// Each language names itself, so a customer can find theirs whatever the UI is
// currently showing. `locale` drives date formatting; Rupiah amounts keep the
// id-ID grouping in every language since the currency never changes.
export const LANGUAGES = [
    { code: 'id', label: 'Bahasa Indonesia', locale: 'id-ID' },
    { code: 'en', label: 'English', locale: 'en-GB' },
    { code: 'ms', label: 'Bahasa Melayu', locale: 'ms-MY' },
];

export const DEFAULT_LANGUAGE = 'id';

const STORAGE_KEY = 'lang';
const DICTIONARIES = { id, en, ms };

const isLanguage = (code) => Object.prototype.hasOwnProperty.call(DICTIONARIES, code);

// Storage can throw in private windows or with site data blocked; the app
// still works there, it just forgets the choice on reload.
const readStoredLanguage = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return isLanguage(stored) ? stored : DEFAULT_LANGUAGE;
    } catch (e) {
        return DEFAULT_LANGUAGE;
    }
};

const lookup = (dict, key) =>
    key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dict);

const interpolate = (text, vars) =>
    vars ? text.replace(/\{(\w+)\}/g, (match, name) => (vars[name] ?? match)) : text;

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(readStoredLanguage);

    const setLang = useCallback((code) => {
        if (!isLanguage(code)) return;
        setLangState(code);
        try {
            localStorage.setItem(STORAGE_KEY, code);
        } catch (e) {
            // Not persisted; the in-memory choice still applies.
        }
    }, []);

    useEffect(() => {
        document.documentElement.lang = lang;
    }, [lang]);

    // Missing keys fall back to Indonesian, then to the key itself, so a gap in
    // one dictionary shows the default copy instead of a blank.
    const t = useCallback((key, vars) => {
        let value = lookup(DICTIONARIES[lang], key);
        if (value === undefined) value = lookup(DICTIONARIES[DEFAULT_LANGUAGE], key);
        if (value === undefined) {
            if (process.env.NODE_ENV !== 'production') console.warn(`[i18n] missing key: ${key}`);
            return key;
        }
        return typeof value === 'string' ? interpolate(value, vars) : value;
    }, [lang]);

    const value = useMemo(() => ({
        lang,
        setLang,
        t,
        locale: LANGUAGES.find((l) => l.code === lang).locale,
    }), [lang, setLang, t]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
    return ctx;
}
